import { StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { Button, FormInput, showErrorToUser, spacing, useForm, Toast } from '@pletly/ui';
import { RootStackParamList } from '../../../../app/UnauthenticatedApp/types';
import { PasswordRequirementsHint, Password, AuthContainer } from '../../../../';
import { withTranslation } from '../../../../hoc/withTranslation';
import { API_URL } from '../../../../config';
import { passwordSchema } from '../../../../consts';
import { getCallbackUrl, setCallbackUrl, useUser } from '../../../../context';

interface Props {
	onSubmitPassword: (password: string) => Promise<unknown> | undefined;
	from?: 'web' | 'app';
}

const formSchema = z.object({
	password: passwordSchema,
});

const PasswordSetComponent = ({ onSubmitPassword, from }: Props) => {
	const { t } = useTranslation();
	const { replace } = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const formMethods = useForm(formSchema);
	const user = useUser();

	const handlePasswordChange: Parameters<typeof formMethods.handleSubmit>['0'] = async ({
		password,
	}) => {
		try {
			await onSubmitPassword(password);

			const isUserLoggedAlready = !!user;
			if (isUserLoggedAlready) {
				Toast.show({
					text1: t('login.password-set-successfully'),
					text2: t('login.you-can-login-now'),
				});

				if (await getCallbackUrl()) {
					await setCallbackUrl(undefined);

					return window.location.replace('/');
				}

				return;
			}

			if (from === 'web') {
				return replace('Login', { passwordReset: 'success' });
			}

			window.open(`${API_URL.replace('/graphql', '')}/auth/resetPasswordDone`, '_self');
		} catch (error) {
			showErrorToUser(error as Error);
		}
	};
	const onSubmit = formMethods.handleSubmit(handlePasswordChange);

	const newPassword = formMethods.watch('password');

	return (
		<AuthContainer title={t('login.set-new-password')} subtitle={t('login.create-new-password')}>
			<FormProvider {...formMethods}>
				<FormInput
					testID="new-password"
					name="password"
					textContentType="newPassword"
					InputComponent={Password}
					gutter
					placeholder={t('login.set-new-password')}
					onSubmitEditing={onSubmit}
				/>
				<PasswordRequirementsHint value={newPassword} />

				<Button
					color="primary"
					size="large"
					variant="filled"
					style={styles.send}
					onPress={onSubmit}
				>
					{t('common.save')}
				</Button>
			</FormProvider>
		</AuthContainer>
	);
};

const styles = StyleSheet.create({
	send: {
		marginTop: spacing(2),
	},
});

export const PasswordSet = withTranslation(PasswordSetComponent);
